import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/theme.css";
import "../css/addCreditor.css"; // for ac-container styles

const API_BASE = "https://transactions.arihantauto.in/api";

const CreditorList = () => {
  const [creditors, setCreditors] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchCreditors();
  }, []);

  const fetchCreditors = async (query = "") => {
    try {
      const res = await axios.get(
        `${API_BASE}/get_creditors.php?q=${encodeURIComponent(query)}`
      );
      setCreditors(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch creditors.");
    }
  };

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    fetchCreditors(q);
  };

  const handleEditClick = (creditor) => {
    if (
      !window.confirm(
        `Do you want to change any details for ${creditor.code}?`
      )
    )
      return;

    // clone and normalize some fields
    setEditingId(creditor.id);
    setEditData({
      ...creditor,
      phone_number: creditor.phone_number || "",
      holding_limit: creditor.holding_limit ?? "",
      credit_days: creditor.credit_days ?? "",
      committed_amount: creditor.committed_amount ?? "",
      is_special: !!creditor.is_special,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const isTenDigitOrEmpty = (val) =>
    !val || val.trim() === "" || /^\d{10}$/.test(val.trim());

  const validateEditData = () => {
    if (!editData.name || !editData.name.trim()) {
      alert("Name is required.");
      return false;
    }

    if (
      editData.phone_number &&
      !/^\d{10}$/.test(editData.phone_number.trim())
    ) {
      alert("Phone number must be exactly 10 digits.");
      return false;
    }

    if (
      editData.holding_limit !== "" &&
      editData.holding_limit !== null &&
      (isNaN(editData.holding_limit) ||
        Number(editData.holding_limit) < 0)
    ) {
      alert("Holding limit must be a non-negative number.");
      return false;
    }

    if (
      editData.credit_days !== "" &&
      editData.credit_days !== null &&
      (!Number.isInteger(Number(editData.credit_days)) ||
        Number(editData.credit_days) < 0)
    ) {
      alert("Credit days must be a non-negative whole number.");
      return false;
    }

    if (
      editData.committed_amount !== "" &&
      editData.committed_amount !== null &&
      isNaN(editData.committed_amount)
    ) {
      alert("Committed amount must be a number.");
      return false;
    }

    // optional WhatsApps: if filled, must be 10 digits
    for (let i = 1; i <= 4; i++) {
      const w = editData[`whatsapp${i}`];
      if (!isTenDigitOrEmpty(w)) {
        alert(`WhatsApp ${i} must be 10 digits if provided.`);
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateEditData()) return;

    if (
      !window.confirm(
        `Do you want to save changes for creditor ${editData.code}?`
      )
    )
      return;

    const payload = {
      ...editData,
      // normalize numeric & boolean types for API / DB
      holding_limit:
        editData.holding_limit === "" || editData.holding_limit == null
          ? null
          : Number(editData.holding_limit),
      credit_days:
        editData.credit_days === "" || editData.credit_days == null
          ? null
          : parseInt(editData.credit_days, 10),
      committed_amount:
        editData.committed_amount === "" ||
        editData.committed_amount == null
          ? null
          : Number(editData.committed_amount),
      is_special: editData.is_special ? 1 : 0,
    };

    try {
      await axios.put(`${API_BASE}/creditors.php`, payload);
      alert("Creditor updated successfully!");
      setEditingId(null);
      setEditData({});
      fetchCreditors(searchQuery);
    } catch (err) {
      console.error(err);
      alert("Failed to update creditor.");
    }
  };

  // const handleDelete = async (code) => {
  //   if (
  //     !window.confirm(
  //       `Are you sure you want to delete creditor ${code}?`
  //     )
  //   )
  //     return;
  //   try {
  //     await axios.delete(`${API_BASE}/creditors.php/${code}`);
  //     alert("Creditor deleted successfully!");
  //     setCreditors((prev) => prev.filter((c) => c.code !== code));
  //   } catch (err) {
  //     console.error(err);
  //     alert("Failed to delete creditor.");
  //   }
  // };

  const handleExportCSV = () => {
    if (!creditors.length) return alert("No data to export.");
    const headers = Object.keys(creditors[0]);
    const rows = creditors.map((c) =>
      headers.map((h) => `"${c[h] ?? ""}"`).join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "creditors.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="ac-container">
      {/* Search + Export */}
      <div className="d-flex mb-3" style={{ gap: "0.75rem" }}>
        <input
          type="text"
          placeholder="Search by Code + Name / Code / Name"
          value={searchQuery}
          onChange={handleSearchChange}
          className="form-control"
        />
        <button
          className="btn btn-primary fw-semibold"
          onClick={handleExportCSV}
        >
          Export CSV
        </button>
      </div>

      {/* Creditors Grid */}
      <div className="row">
        {creditors.map((c) => (
          <div key={c.id} className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="card border-orange p-3 h-100">
              {editingId === c.id ? (
                <>
                  {/* Editing Mode */}
                  <h5 className="mb-2">
                    Edit: {editData.name} ({editData.code})
                  </h5>

                  <div className="mb-2">
                    <label className="fw-semibold">Name</label>
                    <input
                      name="name"
                      value={editData.name || ""}
                      onChange={handleChange}
                      className="form-control mb-2"
                    />
                    <label className="fw-semibold">Phone Number</label>
                    <input
                      name="phone_number"
                      value={editData.phone_number || ""}
                      onChange={handleChange}
                      className="form-control mb-2"
                    />
                    <label className="fw-semibold">Address</label>
                    <input
                      name="address"
                      value={editData.address || ""}
                      onChange={handleChange}
                      className="form-control mb-2"
                    />
                  </div>

                  {/* Contacts 1–4 */}
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="mb-2">
                      <p className="mb-1 fw-semibold">
                        Contact {i}
                      </p>
                      <input
                        name={`contact_name${i}`}
                        value={editData[`contact_name${i}`] || ""}
                        onChange={handleChange}
                        placeholder="Name"
                        className="form-control mb-1"
                      />
                      <input
                        name={`designation${i}`}
                        value={editData[`designation${i}`] || ""}
                        onChange={handleChange}
                        placeholder="Designation"
                        className="form-control mb-1"
                      />
                      <input
                        name={`whatsapp${i}`}
                        value={editData[`whatsapp${i}`] || ""}
                        onChange={handleChange}
                        placeholder="WhatsApp"
                        className="form-control"
                      />
                    </div>
                  ))}

                  {/* Financial Info */}
                  <div className="mb-2">
                    <label className="fw-semibold">
                      Preferred Bank
                    </label>
                    <input
                      name="preferred_bank"
                      value={editData.preferred_bank || ""}
                      onChange={handleChange}
                      className="form-control mb-1"
                    />
                    <label className="fw-semibold">
                      Holding Limit
                    </label>
                    <input
                      type="number"
                      name="holding_limit"
                      value={editData.holding_limit || ""}
                      onChange={handleChange}
                      className="form-control mb-1"
                    />
                    <label className="fw-semibold">
                      Credit Days
                    </label>
                    <input
                      type="number"
                      name="credit_days"
                      value={editData.credit_days || ""}
                      onChange={handleChange}
                      className="form-control mb-1"
                    />
                    <label className="fw-semibold">
                      Committed Amount
                    </label>
                    <input
                      type="number"
                      name="committed_amount"
                      value={editData.committed_amount || ""}
                      onChange={handleChange}
                      className="form-control mb-1"
                    />
                    {/* <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        name="is_special"
                        checked={!!editData.is_special}
                        onChange={handleChange}
                        className="form-check-input"
                        id={`special-${c.id}`}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`special-${c.id}`}
                      >
                        Is Special?
                      </label>
                    </div> */}
                  </div>

                  {/* Notes */}
                  <div className="mb-2">
                    <label className="fw-semibold">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      value={editData.notes || ""}
                      onChange={handleChange}
                      className="form-control"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex mt-2" style={{ gap: "0.5rem" }}>
                    <button
                      className="btn btn-success"
                      onClick={handleSave}
                      type="button"
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleCancel}
                      type="button"
                    >
                      Cancel
                    </button>
                    {/* <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(c.code)}
                      type="button"
                    >
                      Delete
                    </button> */}
                  </div>
                </>
              ) : (
                <>
                  {/* Display Mode */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="mb-0">
                      {c.name} ({c.code})
                    </h5>
                    {c.is_special ? (
                      <span className="badge bg-warning text-dark">
                        Special
                      </span>
                    ) : null}
                  </div>

                  <p className="mb-1">
                    <strong>Phone:</strong> {c.phone_number || "-"}
                  </p>
                  <p className="mb-1">
                    <strong>Address:</strong> {c.address || "-"}
                  </p>

                  {[1, 2, 3, 4].map(
                    (i) =>
                      (c[`contact_name${i}`] ||
                        c[`whatsapp${i}`]) && (
                        <p key={i} className="mb-1">
                          <strong>Contact {i}:</strong>{" "}
                          {c[`contact_name${i}`] || "-"}{" "}
                          {c[`designation${i}`]
                            ? `(${c[`designation${i}`]})`
                            : ""}
                          {c[`whatsapp${i}`]
                            ? ` - ${c[`whatsapp${i}`]}`
                            : ""}
                        </p>
                      )
                  )}

                  <p className="mb-1">
                    <strong>Preferred Bank:</strong>{" "}
                    {c.preferred_bank || "-"}
                  </p>
                  <p className="mb-1">
                    <strong>Holding Limit:</strong>{" "}
                    {c.holding_limit != null
                      ? `₹${c.holding_limit}`
                      : "-"}
                  </p>
                  <p className="mb-1">
                    <strong>Credit Days:</strong>{" "}
                    {c.credit_days != null
                      ? c.credit_days
                      : "-"}
                  </p>
                  <p className="mb-1">
                    <strong>Committed Amount:</strong>{" "}
                    {c.committed_amount != null
                      ? `₹${c.committed_amount}`
                      : "-"}
                  </p>
                  {c.notes && (
                    <p className="mb-1">
                      <strong>Notes:</strong> {c.notes}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex mt-2" style={{ gap: "0.5rem" }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleEditClick(c)}
                      type="button"
                    >
                      Edit
                    </button>
                    {/* <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(c.code)}
                      type="button"
                    >
                      Delete
                    </button> */}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreditorList;